import axios, { AxiosRequestConfig } from 'axios'
import store from '@/store/index'
import { i18n } from '@/plugins/i18n'

axios.interceptors.request.use(function (config) {
    if (!config.headers) {
        config.headers = {}
    }

    if (config.method !== 'get') {
        config.headers['Content-Type'] = 'application/vnd.api+json'
    }

    return config
})

const errorHandler = (error: any, url: string, operation: 'POST' | 'DELETE' | 'GET' | 'PATCH') => {
    const isVbitRes = url.includes('api.vbit.com')
    if (!error.response) {
        return error
    }
    switch (error.response.status) {
        case 401:
            if (store.getters.authenticated && isVbitRes) {
                store.commit('authenticated', false)
            }
            break
        case 400:
        case 412:
        case 404:
            break
        case 500:
            break
        default:
            if (url.includes('vbit')) {
                store.commit('setErrorSnackBar', {
                    data: error.response.data.errors,
                    text: i18n.t('vbit_error.default_header'),
                    url: `${operation}: ${url}`,
                })
            }
    }
    return error
}

const getRequest = (url: string, headers: any = {}, params: any = {}) => {
    let promise = new Promise((resolve, reject) => {
        axios
            .get(url, {
                headers: headers,
                withCredentials: true,
                params: params,
            })
            .then(function (response) {
                resolve({
                    data: response.data,
                })
            })
            .catch(function (error) {
                errorHandler(error, url, 'GET')

                reject({
                    isError: true,
                    isHttpError: true,
                    data: error,
                })
            })
    })
    return promise as Promise<any>
}

const postRequest = (url: string, headers: any, data?: any, params?: any) => {
    let promise = new Promise((resolve, reject) => {
        axios
            .post(url, { data: data }, { headers: headers, withCredentials: true, params: params })
            .then((response) => {
                resolve({
                    data: response.data,
                })
            })
            .catch((error) => {
                errorHandler(error, url, 'POST')

                reject({
                    isError: true,
                    isHttpError: true,
                    data: error,
                })
            })
    })
    return promise as Promise<any>
}

const postFileRequest = (url: string, headers: any, data?: any) => {
    let promise = new Promise((resolve, reject) => {
        axios
            .post(url, data, { headers: headers, withCredentials: true })
            .then((response) => {
                resolve({
                    data: response,
                })
            })
            .catch((error) => {
                errorHandler(error, url, 'POST')

                reject({
                    isError: true,
                    isHttpError: true,
                    data: error,
                })
            })
    })
    return promise as Promise<any>
}

/**
 * Same as patchRequest but with a different axios config
 * This enables consumer to pass in a different config
 * @param url
 * @param headers
 * @param data
 * @returns
 */
const patchRequestV2 = (url: string, data: any, config: AxiosRequestConfig) => {
    let promise = new Promise((resolve, reject) => {
        axios
            .patch(url, { data: data }, { withCredentials: true, ...config })
            .then((response) => {
                resolve({
                    data: response.data,
                })
            })
            .catch((error) => {
                errorHandler(error, url, 'PATCH')

                reject({
                    isError: true,
                    isHttpError: true,
                    data: error,
                })
            })
    })
    return promise as Promise<any>
}
const patchRequest = (url: string, headers: any, data?: any, params?: any) => {
    let promise = new Promise((resolve, reject) => {
        axios
            .patch(url, { data: data }, { headers: headers, withCredentials: true, params: params })
            .then((response) => {
                resolve({
                    data: response.data,
                })
            })
            .catch((error) => {
                errorHandler(error, url, 'PATCH')

                reject({
                    isError: true,
                    isHttpError: true,
                    data: error,
                })
            })
    })
    return promise as Promise<any>
}

const deleteRequest = (url: string, headers: any = {}, data?: any) => {
    let promise = new Promise((resolve, reject) => {
        axios
            .delete(url, { data: { data: data }, headers: headers, withCredentials: true })
            .then((response) => {
                resolve({
                    data: response.data,
                })
            })
            .catch((error) => {
                errorHandler(error, url, 'DELETE')

                reject({
                    isError: true,
                    isHttpError: true,
                    data: error,
                })
            })
    })
    return promise as Promise<any>
}

const downloadFile = (url: string, headers: any, params?: any) => {
    let promise = new Promise((resolve, reject) => {
        axios
            .get(url, {
                headers: headers,
                withCredentials: true,
                params: params,
                responseType: 'blob',
            })
            .then(function (response) {
                const blob = new Blob([response.data], { type: response.data.type })
                const link = document.createElement('a')
                link.href = URL.createObjectURL(blob)
                link.setAttribute('download', response.data.type)
                document.body.appendChild(link)
                link.click()
                resolve({
                    data: response.data,
                })
            })
            .catch(function (error) {
                errorHandler(error, url, 'GET')

                reject({
                    isError: true,
                    isHttpError: true,
                    data: error,
                })
            })
    })
    return promise as Promise<any>
}

const download = (url: string, headers: any, filename: string) => {
    let promise = new Promise((resolve, reject) => {
        axios
            .get(url, {
                headers: headers,
                withCredentials: true,
                responseType: 'blob',
            })
            .then(function (response) {
                const url = window.URL.createObjectURL(new Blob([response.data]))
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', filename)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                // const blob = new Blob([response.data], {type: response.data.type })
                // const link = document.createElement('a')
                // link.href = URL.createObjectURL(blob)
                // link.setAttribute('download', filename)
                // document.body.appendChild(link)
                // link.click()
                resolve({
                    data: response.data,
                })
            })
            .catch(function (error) {
                errorHandler(error, url, 'GET')

                reject({
                    isError: true,
                    isHttpError: true,
                    data: error,
                })
            })
    })

    return promise as Promise<any>
}

const downloadFileObject = (url: string, headers: any) => {
    let promise = new Promise((resolve, reject) => {
        axios
            .get(url, {
                headers: headers,
                withCredentials: true,
                responseType: 'blob',
            })
            .then(function (response) {
                resolve({
                    data: response.data,
                })
            })
            .catch(function (error) {
                errorHandler(error, url, 'GET')
                reject({
                    isError: true,
                    isHttpError: true,
                    data: error,
                })
            })
    })

    return promise as Promise<any>
}

export {
    getRequest,
    postRequest,
    postFileRequest,
    patchRequest,
    patchRequestV2,
    deleteRequest,
    downloadFile,
    download,
    downloadFileObject,
}
